import React, { Fragment, useState, useEffect, useRef } from 'react'
import { Row, Col, Button, Table, OverlayTrigger, Tooltip, ButtonGroup, InputGroup, FormControl, Card } from 'react-bootstrap'
import formatCpf from '@brazilian-utils/format-cpf'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPowerOff, faEye, faSearch, faArrowRight, faPhone, faEnvelope, faCommentDots } from '@fortawesome/free-solid-svg-icons'
import Loader from 'react-loader-spinner'

import MenuContext from '../../contexts/MenuContexts'
import PageLoading from '../Common/PageLoading'
import PacientesService from '../../services/paciente.service'
import PermissoesConstants from '../../constants/permissoes.constants'

export default function PacienteList(props) {
  let acoesPermitidas = {}
  const [dadosPacientes, setDadosPacientes] = useState([])
  const [showInativos, setShowInativos] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingPagina, setIsLoadingPagina] = useState(false)
  const [pacienteFilter, setPacienteFilter] = useState('')
  const [proximaPagina, setProximaPagina] = useState(1)
  const [totalPaginas, setTotalPaginas] = useState(0)
  const pagina = useRef(1)
  const totalPaginasRef = useRef(0)

  useEffect(() => {
    totalPaginasRef.current = totalPaginas
  }, [totalPaginas])

  useEffect(() => {
    const onScroll = (e) => {
      if (totalPaginasRef.current <= 0) return
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
        const p = pagina.current + 1
        if (p > totalPaginasRef.current) return
        pagina.current = p
        setProximaPagina(p)
      }
    }
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [])

  useEffect(() => {
    if (proximaPagina <= 1) return
    if (proximaPagina > totalPaginas) return
    fetchPacientes(proximaPagina)
  }, [proximaPagina])

  useEffect(() => {
    pagina.current = 1
    setProximaPagina(1)
    fetchPrimeiraPaginaPacientes()
  }, [showInativos])

  const montarFiltros = () => [
    { campo: 'pacienteNome', valor: pacienteFilter },
    { campo: 'pacienteAtivo', valor: String(!showInativos) },
  ]

  const fetchPrimeiraPaginaPacientes = async () => {
    try {
      setIsLoading(true)
      const filtros = montarFiltros()
      const { data } = await PacientesService.getPacientesListaV2(filtros, 1, 20)
      const tp = data.totalPaginas || 0
      setTotalPaginas(tp)
      totalPaginasRef.current = tp
      setDadosPacientes(data.dados || [])
      pagina.current = 1
      setProximaPagina(1)
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
    }
  }

  const fetchPacientes = async (p) => {
    try {
      if (p > totalPaginasRef.current) return

      setIsLoadingPagina(true)
      const filtros = montarFiltros()
      const { data } = await PacientesService.getPacientesListaV2(filtros, p, 20)
      const novos = data.dados || []

      setDadosPacientes((prev) => (p === 1 ? novos : [...prev, ...novos]))
      setIsLoadingPagina(false)
    } catch (e) {
      setIsLoadingPagina(false)
    }
  }

  const handleFilterPaciente = (e) => {
    setPacienteFilter(e.target.value)
  }

  const toggleInativos = (mostrarInativos) => {
    setShowInativos(mostrarInativos)
  }

  const editPaciente = (currentId) => {
    props.editarPaciente(currentId)
    props.history.push(`/pacientes/${currentId}`)
  }

  const desativar = async (pacienteId, ativar) => {
    try {
      await PacientesService.desativarPaciente(pacienteId, ativar)
      const idx = dadosPacientes.findIndex((x) => x.id === pacienteId)
      if (idx >= 0) {
        dadosPacientes[idx].ativo = ativar
        setDadosPacientes(dadosPacientes.filter((x) => x.ativo === !showInativos))
      }
    } catch (e) {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOnPress = (e) => {
    if (e.key === 'Enter') {
      pagina.current = 1
      setProximaPagina(1)
      fetchPrimeiraPaginaPacientes()
    }
  }

  const formatCpfSafe = (cpf) => {
    if (!cpf) return '—'
    try {
      return formatCpf(String(cpf).replace(/\D/g, ''))
    } catch {
      return cpf
    }
  }

  const renderAcoes = (paciente) => {
    if (Object.keys(acoesPermitidas).length === 0) return null
    return (
      <div
        className="d-flex justify-content-end flex-wrap"
        style={{ gap: 6 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        {acoesPermitidas[PermissoesConstants.EDITAR] && (
          <OverlayTrigger trigger="hover" placement="top" overlay={<Tooltip>Editar paciente</Tooltip>}>
            <Button onClick={() => editPaciente(paciente.id)} variant="primary" size="sm">
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </OverlayTrigger>
        )}
        {acoesPermitidas[PermissoesConstants.ATIVAR_INATIVAR] && (
          <OverlayTrigger
            trigger="hover"
            placement="top"
            overlay={<Tooltip>{paciente.ativo ? 'Desativar paciente' : 'Ativar paciente'}</Tooltip>}
          >
            <Button onClick={() => desativar(paciente.id, !paciente.ativo)} variant={paciente.ativo ? 'success' : 'danger'} size="sm">
              <FontAwesomeIcon icon={faPowerOff} />
            </Button>
          </OverlayTrigger>
        )}
        {!acoesPermitidas[PermissoesConstants.ATIVAR_INATIVAR] &&
          !acoesPermitidas[PermissoesConstants.EDITAR] &&
          acoesPermitidas[PermissoesConstants.DETALHAR] && (
            <Button title="Detalhes" onClick={() => editPaciente(paciente.id)} variant="primary" size="sm">
              <FontAwesomeIcon icon={faEye} />
            </Button>
          )}
      </div>
    )
  }

  const renderLoading = () => <PageLoading />

  const renderLista = () => (
    <Fragment>
      <style>
        {`
          .pacientes-lista-desktop tbody tr.pacientes-lista-linha:hover td {
            background-color: #eef2f5 !important;
          }
          .pacientes-lista-card:hover {
            box-shadow: 0 0.2rem 0.65rem rgba(0, 0, 0, 0.1) !important;
            border-color: #c5d3e0 !important;
          }
        `}
      </style>
      {dadosPacientes && dadosPacientes.length > 0 ? (
        <div>
          {isLoadingPagina && (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <Loader type="ThreeDots" color="#0881c7" height={48} width={48} />
            </div>
          )}

          <div className="d-md-none">
            {dadosPacientes.map((paciente, index) => (
              <Card
                key={paciente.id || index}
                className="mb-3 shadow-sm border pacientes-lista-card"
                style={{ background: '#fafbfc', cursor: 'pointer' }}
                onClick={() => editPaciente(paciente.id)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    editPaciente(paciente.id)
                  }
                }}
              >
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="pr-2" style={{ minWidth: 0 }}>
                      <div className="font-weight-bold text-dark text-break">{paciente.nome}</div>
                      <div className="small text-muted">{formatCpfSafe(paciente.cpf)}</div>
                    </div>
                    {renderAcoes(paciente)}
                  </div>
                  <div className="small border-top pt-2 mt-2">
                    <div className="mb-1">
                      <FontAwesomeIcon icon={faPhone} className="text-primary mr-2" />
                      {paciente.telefonePrincipal || '—'}
                    </div>
                    <div className="mb-1">
                      <FontAwesomeIcon icon={faCommentDots} className="text-success mr-2" />
                      {paciente.whatsapp || '—'}
                    </div>
                    <div className="text-truncate">
                      <FontAwesomeIcon icon={faEnvelope} className="text-secondary mr-2" />
                      {paciente.email || '—'}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          <div className="d-none d-md-block table-responsive rounded border bg-white shadow-sm">
            <Table hover borderless className="mb-0 pacientes-lista-desktop">
              <thead className="thead-light" style={{ borderBottom: '2px solid #dee2e6' }}>
                <tr>
                  <th
                    className="border-0 align-middle text-uppercase small text-secondary font-weight-bold"
                    style={{ padding: '16px 18px', letterSpacing: '0.04em' }}
                  >
                    Paciente
                  </th>
                  <th
                    className="border-0 align-middle text-uppercase small text-secondary font-weight-bold text-nowrap"
                    style={{ padding: '16px 18px', letterSpacing: '0.04em' }}
                  >
                    CPF
                  </th>
                  <th
                    className="border-0 align-middle text-uppercase small text-secondary font-weight-bold"
                    style={{ padding: '16px 18px', letterSpacing: '0.04em' }}
                  >
                    Contato
                  </th>
                  <th
                    className="border-0 align-middle text-uppercase small text-secondary font-weight-bold d-none d-lg-table-cell"
                    style={{ padding: '16px 18px', letterSpacing: '0.04em' }}
                  >
                    E-mail
                  </th>
                  {Object.keys(acoesPermitidas).length > 0 && (
                    <th
                      className="border-0 align-middle text-right text-uppercase small text-secondary font-weight-bold"
                      style={{ padding: '16px 18px', minWidth: 120, letterSpacing: '0.04em' }}
                    >
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {dadosPacientes.map((paciente, index) => (
                  <Fragment key={paciente.id || index}>
                    <tr
                      className="align-middle pacientes-lista-linha"
                      onClick={() => editPaciente(paciente.id)}
                      style={{
                        cursor: 'pointer',
                        borderBottom:
                          index < dadosPacientes.length - 1 ? '1px solid #e8ecef' : undefined,
                        background: index % 2 === 0 ? '#fff' : '#f8fafb',
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          editPaciente(paciente.id)
                        }
                      }}
                    >
                      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                        <span className="font-weight-medium text-dark">{paciente.nome}</span>
                      </td>
                      <td className="text-muted small text-nowrap" style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                        {formatCpfSafe(paciente.cpf)}
                      </td>
                      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                        <div className="small">
                          <span className="text-muted">Tel.</span> {paciente.telefonePrincipal || '—'}
                        </div>
                        <div className="small">
                          <span className="text-muted">WA</span>{' '}
                          <span className="text-success">{paciente.whatsapp || '—'}</span>
                        </div>
                        <div className="small d-lg-none text-truncate" style={{ maxWidth: 220 }}>
                          <span className="text-muted">@</span> {paciente.email || '—'}
                        </div>
                      </td>
                      <td
                        className="d-none d-lg-table-cell small text-truncate"
                        style={{ padding: '16px 18px', maxWidth: 220, verticalAlign: 'middle' }}
                        title={paciente.email || ''}
                      >
                        {paciente.email || '—'}
                      </td>
                      {Object.keys(acoesPermitidas).length > 0 && (
                        <td className="text-right" style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                          {renderAcoes(paciente)}
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="text-muted py-4">Nenhum paciente encontrado.</div>
      )}
    </Fragment>
  )

  return (
    <MenuContext.Consumer>
      {(acoes) => {
        acoes.forEach((a) => {
          acoesPermitidas[`${a.nome}`] = a
        })
        return (
          <div>
            <Row className="mb-4 align-items-center">
              <Col xs={12} md={4} className="mb-2 mb-md-0">
                <h1 className="h3 m-0 text-secondary font-weight-bold">Pacientes</h1>
              </Col>
              <Col xs={12} md={5}>
                <InputGroup className="mb-0">
                  <FormControl
                    placeholder="Nome, CPF ou RG..."
                    aria-label="Filtrar pacientes"
                    onChange={handleFilterPaciente}
                    value={pacienteFilter}
                    onKeyPress={handleOnPress}
                  />
                  <InputGroup.Append>
                    <Button variant="outline-primary" onClick={fetchPrimeiraPaginaPacientes}>
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col xs={12} md={3} className="d-flex justify-content-md-end mt-2 mt-md-0">
                <ButtonGroup aria-label="Filtrar ativos" className="mr-2" style={{ height: 38 }}>
                  <Button onClick={() => toggleInativos(false)} variant={showInativos ? 'outline-primary' : 'primary'}>
                    Ativos
                  </Button>
                  <Button onClick={() => toggleInativos(true)} variant={!showInativos ? 'outline-primary' : 'primary'}>
                    Inativos
                  </Button>
                </ButtonGroup>
                {acoesPermitidas[PermissoesConstants.ADICIONAR] && (
                  <Button onClick={() => props.history.push(`/pacientes/cadastrar`)} className="text-nowrap">
                    Cadastrar
                  </Button>
                )}
              </Col>
            </Row>
            {isLoading ? renderLoading() : renderLista()}
          </div>
        )
      }}
    </MenuContext.Consumer>
  )
}
